import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { TicketStatus } from '@/constants/ticket-status'
import { cn } from '@/lib/utils'
import {
  listAgentTicketsQuery,
  listDeskTicketsQuery,
  listFinishedTicketsQuery,
  listPendingTicketsQuery,
} from '@/queries/ticket.query'
import {
  selectClearTicket,
  selectCurrentTicketId,
  selectSetDialog,
  useChatStore,
} from '@/stores/chat.store'
import { avatarName, getAvatarColor } from '@/utils/avatar-name'
import { IconArrowLeft } from '@tabler/icons-react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { ArrowRightLeft, Check } from 'lucide-react'
import {
  getCurrentChatTicket,
  isHumanInProgressTicket,
} from '../utils/ticket-routing'

export function ChatHeader() {
  const currentTicketId = useChatStore(selectCurrentTicketId)
  const clearTicket = useChatStore(selectClearTicket)
  const setOpen = useChatStore(selectSetDialog)
  const { data: agentTickets } = useSuspenseQuery(listAgentTicketsQuery)
  const { data: deskTickets } = useSuspenseQuery(listDeskTicketsQuery)
  const { data: pendingTickets } = useSuspenseQuery(listPendingTicketsQuery)
  const { data: finishedTickets } = useSuspenseQuery(listFinishedTicketsQuery)

  const currentTicket = getCurrentChatTicket(
    currentTicketId,
    agentTickets,
    deskTickets,
    pendingTickets,
    finishedTickets
  )

  return (
    <div
      className={cn(
        'absolute left-0 right-0 top-0 z-10 flex flex-none items-center justify-between gap-2 rounded-t-md border-b bg-background/80 p-3 shadow-sm backdrop-blur-md transition-all duration-200 sm:gap-3'
      )}
    >
      <div className='flex min-w-0 flex-1 items-center gap-2 sm:gap-3'>
        <Button
          size='icon'
          variant='ghost'
          className='-ml-2 shrink-0 sm:hidden'
          onClick={() => clearTicket()}
        >
          <IconArrowLeft />
        </Button>
        <button
          type='button'
          onClick={() => setOpen('editContact')}
          className='flex min-w-0 flex-1 items-center gap-2 rounded-md p-1 transition-colors hover:bg-secondary-foreground/10 sm:flex-none'
        >
          <Avatar className='size-10 shrink-0'>
            <AvatarImage
              src={currentTicket?.contact.profilePicUrl}
              alt={currentTicket?.contact.name}
            />
            <AvatarFallback
              className={cn(
                getAvatarColor(currentTicket?.contact.name),
                'text-white'
              )}
            >
              {avatarName(currentTicket?.contact.name || '')}
            </AvatarFallback>
          </Avatar>
          <div className='min-w-0 flex-1 text-left sm:flex-none'>
            <span className='block truncate text-sm font-medium'>
              {currentTicket?.contact.name}
            </span>
            <span className='block truncate text-xs text-muted-foreground sm:max-w-32'>
              {currentTicket?.contact.phone}
            </span>
          </div>
        </button>
      </div>

      <div className='flex shrink-0 items-center gap-1.5 sm:gap-2'>
        {(currentTicket?.status === TicketStatus.IN_PROGRESS ||
          currentTicket?.status === TicketStatus.PENDING) && (
          <Button
            size='sm'
            variant='outline'
            className='gap-1.5 px-2 sm:gap-2 sm:px-3'
            onClick={() => setOpen('transfer')}
          >
            <ArrowRightLeft size={16} className='shrink-0' />
            <span className='hidden sm:inline'>Transferir</span>
          </Button>
        )}
        {isHumanInProgressTicket(currentTicket) && (
          <Button
            size='sm'
            variant='destructive'
            className='gap-1.5 px-2 sm:gap-2 sm:px-3'
            onClick={() => setOpen('finalize')}
          >
            <Check size={16} className='shrink-0' />
            <span className='hidden sm:inline'>Finalizar</span>
          </Button>
        )}
      </div>
    </div>
  )
}
